import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// Generate or retrieve a persistent visitor ID
const getVisitorId = (): string => {
  const storageKey = "visitor_id";
  let visitorId = localStorage.getItem(storageKey);
  
  if (!visitorId) {
    visitorId = `v_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem(storageKey, visitorId);
  }
  
  return visitorId;
};

// Fetch country info with fallback providers
const GEO_CACHE_KEY = "geo_data";
const GEO_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

type GeoResult = {
  country?: string;
  country_code?: string;
  city?: string;
  region?: string;
};

const fetchGeoLocation = async (): Promise<GeoResult> => {
  // Check cache first
  try {
    const cached = localStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      const { data, ts } = JSON.parse(cached);
      if (Date.now() - ts < GEO_CACHE_TTL) return data;
    }
  } catch {}

  // All client-side geo APIs are CORS-blocked from preview domains.
  // Skip geo lookup entirely — analytics will record visits without location data.
  // Location data can be enriched server-side if needed.
  return {};
};

export function useVisitorTracking() {
  const location = useLocation();
  const trackedPaths = useRef<Set<string>>(new Set());
  const sessionId = useRef<string>(`session_${Date.now()}`);

  useEffect(() => {
    const trackVisit = async () => {
      const pathKey = `${sessionId.current}_${location.pathname}`;
      
      // Don't track the same page twice in the same session
      if (trackedPaths.current.has(pathKey)) {
        return;
      }
      
      trackedPaths.current.add(pathKey);

      const visitorId = getVisitorId();
      const geoData = await fetchGeoLocation();

      try {
        await supabase.from("visitor_analytics").insert({
          visitor_id: visitorId,
          page_path: location.pathname,
          country: geoData.country || null,
          country_code: geoData.country_code || null,
          city: geoData.city || null,
          region: geoData.region || null,
          user_agent: navigator.userAgent,
          referrer: document.referrer || null,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
        });
      } catch (error) {
        console.error("Failed to track visit:", error);
      }
    };

    trackVisit();
  }, [location.pathname]);
}
